import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/wolfApi";

function diff(toIso) {
  const t = new Date(toIso).getTime() - Date.now();
  if (t <= 0) return { live: true, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t / 3600000) % 24);
  const m = Math.floor((t / 60000) % 60);
  const s = Math.floor((t / 1000) % 60);
  return { live: false, d, h, m, s };
}

function pad(n) { return String(n).padStart(2, "0"); }

function CountdownUnit({ value, label }) {
  return (
    <div className="text-center">
      <p className="font-display tracking-tight text-3xl sm:text-4xl text-white tabular-nums leading-none">
        {pad(value)}
      </p>
      <p className="text-[9px] uppercase tracking-[0.32em] text-white/40 mt-1">{label}</p>
    </div>
  );
}

export default function PackDropCalendar() {
  const [drops, setDrops] = useState([]);
  const [, setTick] = useState(0);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/den/drops")
      .then((r) => { if (alive) setDrops(r.data.drops || []); })
      .catch((e) => alive && setErr(e.message || "Failed"));
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (err || drops.length === 0) return null;

  const [next, ...rest] = drops;
  const nextDiff = diff(next.drops_at);

  return (
    <section className="mb-8" data-testid="drop-calendar">
      {/* HERO DROP */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden border border-[#D4AF37]/30 bg-[#111111]"
        data-testid="drop-calendar-next"
      >
        <div className="absolute inset-0 opacity-30">
          <img src={next.img} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111] via-[#111111]/85 to-transparent" />
        <div className="relative p-6 sm:p-8 lg:p-10 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[#D4AF37]/90">
              <Calendar size={14} />
              <p className="text-[10px] uppercase tracking-[0.32em]">— Next pack drop</p>
            </div>
            <h3 className="font-display text-4xl sm:text-5xl text-white mt-3 leading-[0.95]">
              {next.title}
            </h3>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80 mt-2">{next.kind}</p>
            <p className="font-editorial italic text-white/65 mt-3 max-w-md">{next.blurb}</p>
          </div>
          <div className="lg:col-span-5">
            {nextDiff.live ? (
              <div className="text-center lg:text-right" data-testid="drop-live">
                <p className="font-display tracking-extreme text-[#D4AF37] text-2xl animate-pulse">— LIVE NOW</p>
                <Link
                  to="/music"
                  data-testid="drop-live-cta"
                  className="inline-flex items-center gap-2 mt-4 bg-transparent text-[#D4AF37] border border-[#D4AF37]/70 font-display tracking-extreme text-sm px-6 py-3 hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37]"
                >
                  Open the drop <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <div className="flex justify-center lg:justify-end gap-3 sm:gap-6" data-testid="drop-countdown">
                <CountdownUnit value={nextDiff.d} label="Days" />
                <span className="font-display text-3xl sm:text-4xl text-white/20">:</span>
                <CountdownUnit value={nextDiff.h} label="Hrs" />
                <span className="font-display text-3xl sm:text-4xl text-white/20">:</span>
                <CountdownUnit value={nextDiff.m} label="Min" />
                <span className="font-display text-3xl sm:text-4xl text-white/20">:</span>
                <CountdownUnit value={nextDiff.s} label="Sec" />
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* UPCOMING LIST */}
      {rest.length > 0 && (
        <div className="mt-3 border border-white/10 bg-[#111111]" data-testid="drop-calendar-upcoming">
          <div className="px-6 py-3 border-b border-white/10 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/80">— On deck</p>
            <Link to="/merch" data-testid="drop-calendar-merch-link" className="text-[10px] uppercase tracking-[0.32em] text-white/55 hover:text-[#D4AF37]">
              The shelf →
            </Link>
          </div>
          <ul>
            {rest.map((d) => {
              const x = diff(d.drops_at);
              return (
                <li
                  key={d.id}
                  data-testid={`drop-item-${d.id}`}
                  className="flex items-center gap-4 px-6 py-4 border-b border-white/5 last:border-b-0"
                >
                  <div className="w-12 h-12 shrink-0 overflow-hidden border border-white/10">
                    <img src={d.img} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg text-white tracking-tight truncate">{d.title}</p>
                    <p className="text-[10px] uppercase tracking-[0.32em] text-white/40 mt-0.5">{d.kind}</p>
                  </div>
                  {x.live ? (
                    <span className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-1">Live</span>
                  ) : (
                    <p className="font-body tabular-nums text-white/70 text-sm">
                      {x.d}d {pad(x.h)}h {pad(x.m)}m
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/35">
        <Lock size={11} className="text-[#D4AF37]/60" />
        Drops visible only inside The Den.
      </div>
    </section>
  );
}
